import { z } from 'zod';

import contenu from '@/content/contenu';

/**
 * Le schema de la demande de place, ECRIT UNE FOIS ET IMPORTE DES DEUX COTES.
 *
 * `CONVENTIONS.md` § « Un seul schema de validation, partage » le prescrit nommement, et son
 * contre-exemple dit pourquoi : deux jeux de regles, l'un dans le composant et l'autre dans la
 * route, divergent au premier changement. Le navigateur gagne alors toujours, et le controle du
 * serveur cesse d'en etre un.
 *
 * `AC-FRM-010` MESURE CE PARTAGE PAR SON EFFET, et non en lisant les imports : il envoie a la route
 * six requetes que le navigateur aurait refusees, et exige qu'elle refuse les six.
 *
 * LES OPTIONS DE DISPONIBILITE NE SONT PAS RECOPIEES ICI. Elles viennent de `content/contenu.ts`,
 * qui les tient de `contenu-site.md` §17. Les ecrire une seconde fois aurait cree un second endroit
 * ou une option peut changer : une valeur cochee sur la page et refusee par le serveur donnerait un
 * echec que rien n'explique au lecteur.
 *
 * IL NE PORTE AUCUN TEXTE AFFICHE. Les messages d'erreur montres au lecteur vivent sous
 * `contenu.interface`, recopies de `contenu-site.md` § Les libelles d'interface, et c'est le
 * composant qui les pose. Ce fichier ne porte que des CODES d'erreur.
 */
/*
 * LES OPTIONS VIENNENT DU BLOC DU PARCOURS DEPUIS LE 2026-08-15, TACHE 07-07, ET NON PLUS DE LA
 * SECTION 17.
 *
 * CE QUI A CHANGE : la section 17 a perdu ses six champs le meme jour, decision de Wahid, et
 * `contenu-site.md` v5 a supprime leur texte. `contenu.sections.leFormulaire.disponibilites`
 * n'existe donc plus. Le commentaire de `schemaInscription`, plus bas, avait annonce ce jour-la et
 * dit ce qu'il casserait.
 *
 * CE QUE CELA NE CHANGE PAS : les cinq options. `contenu-site.md` les ecrivait mot pour mot des deux
 * cotes, et `schemaInscription` puisait deja dans le bloc du parcours. Les deux schemas lisent donc
 * la meme liste, ce qui etait deja le cas en valeur.
 */
const disponibilites = contenu.inscription.etapes.vosCoordonnees.disponibilites;

const OPTIONS_QUAND: readonly string[] = disponibilites.quand.options;
const OPTIONS_JOUR: readonly string[] = disponibilites.quelJour.options;

/**
 * Les codes d'erreur du schema, et leur unique fonction : dire QUEL message afficher.
 *
 * Ils ne s'affichent jamais. Le composant les traduit en libelles de `contenu.interface`, et la
 * route ne les rend pas au navigateur : une reponse d'API qui detaille ce qui a echoue renseigne
 * autant un robot qu'un lecteur.
 */
export const CODES = {
  obligatoire: 'obligatoire',
  email: 'email',
  consentement: 'consentement',
  option: 'option',
  piege: 'piege',
} as const;

/**
 * Une liste de cases cochees : chaque valeur vient de la liste de la spec, ou la demande est refusee.
 *
 * LE PRETRAITEMENT N'EST PAS UNE PRECAUTION, C'EST LA CORRECTION D'UN DEFAUT MESURE LE 2026-08-15.
 * `react-hook-form` rend un groupe de cases a cocher sous deux formes selon son etat : un TABLEAU des
 * valeurs cochees quand il y en a, et le BOOLEEN `false` quand il n'y en a aucune. Sans ce
 * pretraitement, un groupe vide echouait donc a la validation de forme, et le formulaire affichait
 * « Ce champ est necessaire pour vous ecrire » sous un champ que `contenu-site.md` §17 declare
 * FACULTATIF. Une personne qui ne cochait aucune disponibilite ne pouvait pas envoyer sa demande.
 *
 * CE QU'IL NE FAIT PAS : accepter n'importe quoi. Ce qui n'est pas un tableau devient un tableau
 * vide, et le contenu reste verifie contre les options de la spec.
 */
function groupeDeCases(options: readonly string[]) {
  return z.preprocess(
    (valeur) => (Array.isArray(valeur) ? valeur : []),
    z
      .array(z.string())
      .default([])
      .refine((valeurs) => valeurs.every((element) => options.includes(element)), {
        message: CODES.option,
      }),
  );
}

export const schemaDemande = z.object({
  prenomNom: z.string().trim().min(1, CODES.obligatoire),
  email: z.string().trim().min(1, CODES.obligatoire).email(CODES.email),
  telephone: z.string().trim().min(1, CODES.obligatoire),
  ville: z.string().trim().min(1, CODES.obligatoire),
  dispoQuand: groupeDeCases(OPTIONS_QUAND),
  dispoJour: groupeDeCases(OPTIONS_JOUR),
  consentement: z.literal(true, { message: CODES.consentement }),

  /*
   * LE CHAMP PIEGE. Il n'est nomme dans aucune spec : `spec-technique.md` ecrit « elle rejette la
   * requete si le champ piege est rempli », sans dire lequel. Son nom est donc un choix du dev, et
   * il ne s'affiche pas.
   *
   * IL EST VALIDE PAR LE MEME SCHEMA QUE LE RESTE, et ce n'est pas une elegance : le mettre a part
   * dans la route aurait fait deux endroits ou une demande peut etre refusee, et le composant
   * client n'aurait eu aucun moyen de savoir que ce champ compte.
   */
  piege: z.string().max(0, CODES.piege).optional().default(''),

  /*
   * LA SESSION CHOISIE, quand la page en propose au moins une.
   *
   * ELLE EST FACULTATIVE, ET ELLE LE RESTERA : tant qu'aucune session n'est ouverte, la page n'affiche
   * pas ce choix, et une demande sans session est une demande valide. C'est aussi le cas de quelqu'un
   * qu'aucune date n'arrange, a qui la derniere case le permet.
   *
   * SA VALEUR N'EST PAS VERIFIEE CONTRE LES SESSIONS OUVERTES, et il vaut mieux l'ecrire : le schema
   * est statique, il ne lit pas la base. Un identifiant invente est refuse par la cle etrangere de la
   * table, ce qui rend la demande en erreur plutot qu'en silence. Ce que le schema tient : la forme.
   */
  sessionId: z
    .union([z.string().uuid(), z.literal('')])
    /*
     * `.nullish()` ET NON `.optional()`, ET C'EST UN DEFAUT MESURE LE 2026-08-15.
     *
     * `.optional()` accepte l'ABSENCE, pas la valeur `null`. Or le navigateur envoie le resultat de
     * CE MEME schema, dont la transformation ci-dessous rend justement `null` quand aucune session
     * n'est choisie. Le serveur refusait donc en 400 ce que le navigateur venait de valider, et le
     * lecteur voyait le message d'echec technique sur une demande parfaitement formee.
     *
     * CE QUE CE DEFAUT DIT DU MONTAGE, et c'est ce qui vaut d'etre retenu : un schema partage entre
     * les deux cotes n'est pas seulement une commodite, c'est aussi un endroit ou l'entree d'un cote
     * est la SORTIE de l'autre. Les deux formes doivent etre acceptees.
     */
    .nullish()
    .transform((valeur) => (valeur ? valeur : null)),
});

export type Demande = z.infer<typeof schemaDemande>;

/**
 * Le nom du champ piege, pose une fois : le composant le rend, la route ne le lit que par le schema.
 *
 * IL RESSEMBLE A UN CHAMP ORDINAIRE, et c'est tout son propos : un robot qui remplit tout ce qu'il
 * trouve le remplit aussi. Un nom comme `piege` sur l'attribut `name` aurait annonce la couleur.
 */
export const CHAMP_PIEGE = 'piege';

/**
 * Le schema du PARCOURS d'inscription, partage lui aussi par le navigateur et par la route.
 *
 * IL EST DISTINCT DE `schemaDemande` PARCE QUE LE FORMULAIRE L'EST : `contenu-site.md` v4 § Le
 * parcours d'inscription ajoute un septieme champ, la remarque, que la section 17 de la page
 * d'accueil ne porte pas. Etendre le schema existant plutot que le recopier garde une seule regle
 * par champ commun : un changement sur l'email vaut pour les deux formulaires, et il ne peut pas
 * n'en toucher qu'un.
 *
 * SES OPTIONS DE DISPONIBILITE VIENNENT DU BLOC DU PARCOURS, et non de celui de la section 17, bien
 * que les deux listes soient identiques mot pour mot aujourd'hui. Motif datable : la tache `07-07`
 * retire les six champs de la page d'accueil, et le bloc `sections.leFormulaire` disparaitra avec
 * eux. Un schema qui y puiserait ses options cesserait alors de compiler, ou pire, se rabattrait sur
 * une liste vide et refuserait toute case cochee.
 *
 * `AC-INS-002` LE MESURE PAR SON EFFET, comme `AC-FRM-010` mesure l'autre : il poste a la route ce
 * que le navigateur aurait refuse, et exige qu'elle refuse la meme chose.
 */
const disponibilitesDuParcours = contenu.inscription.etapes.vosCoordonnees.disponibilites;

export const schemaInscription = schemaDemande.extend({
  dispoQuand: groupeDeCases(disponibilitesDuParcours.quand.options),
  dispoJour: groupeDeCases(disponibilitesDuParcours.quelJour.options),

  /*
   * LE CONSENTEMENT N'EST PLUS UNE CASE A COCHER DEPUIS LE 2026-08-19, ET LE SCHEMA LE POSE A VRAI.
   *
   * CE QUI A CHANGE : `contenu-site.md` v16 § Etape 2 retire le septieme champ et affiche une
   * mention, « En continuant, vous acceptez de recevoir les informations de la session par email,
   * par WhatsApp ou par telephone ». Le consentement se donne donc EN ENVOYANT le formulaire, et le
   * navigateur n'a plus rien a cocher ni a transmettre.
   *
   * `.default(true)` ET NON UN CHAMP RETIRE : la colonne `consentement` de la table existe toujours,
   * l'administration l'affiche, et l'email de notification en porte la ligne. Retirer la cle du
   * schema aurait fait ecrire `false` a une personne qui a bel et bien consenti.
   *
   * `z.literal(true)` EST CONSERVE SOUS LE DEFAUT, et ce n'est pas une precaution sans objet : une
   * requete qui poste `consentement: false` contredit la mention affichee, et la route la refuse en
   * 400 comme elle le faisait avant. `AC-INS-002` mesure ce refus, et il n'a pas eu a changer.
   */
  consentement: z.literal(true, { message: CODES.consentement }).default(true),

  /*
   * LA REMARQUE EST FACULTATIVE, ET ELLE N'EST PAS BORNEE EN LONGUEUR.
   *
   * `contenu-site.md` v4 la declare « non » obligatoire et n'ecrit aucune limite, et
   * `spec-technique.md` v6 § La route d'inscription non plus. Poser un maximum ici serait inventer
   * une regle : une personne dont la remarque depasserait la borne verrait sa demande refusee par un
   * message que la spec n'ecrit nulle part. La colonne `remarque` de la table est en `text`, qui n'a
   * pas de longueur maximale utile en Postgres.
   */
  remarque: z.string().trim().optional().default(''),

  /*
   * LA VILLE N'EST PLUS OBLIGATOIRE DANS LE PARCOURS DEPUIS LE 2026-08-20, ET ELLE A CHANGE DE SENS.
   *
   * CE QUI A CHANGE : le champ ne s'affiche plus quand une session est retenue. Demande de Wahid :
   * « retirer le champ ville quand le choix de la session est fait et le remplacer par "Ville de
   * preference" quand pas de session disponible ». Quand une date est retenue, la ville est celle de
   * la session, et la redemander recueillerait une valeur qui ne sert a rien.
   *
   * `schemaDemande` GARDE SA VILLE OBLIGATOIRE, et ce n'est pas un oubli : il sert la route
   * `/api/reservation`, dont le formulaire n'a jamais propose de session.
   *
   * CE QUE CE SCHEMA NE PEUT PAS TENIR : « obligatoire quand aucune session n'est retenue ». Cette
   * regle croise deux champs, et un `superRefine` rendrait un `ZodEffects`, qui n'a ni `.shape` ni
   * `.pick()`. Or `app/api/inscription/route.ts` enumere `.shape` pour savoir ce qu'il ecrit, et le
   * parcours derive son etape 2 par `.pick()`. La regle croisee vit donc dans `schemaDeLaVille`
   * ci-dessous, que le parcours applique quand il affiche le champ.
   */
  ville: z.string().trim().optional().default(''),
});

export type DemandeDInscription = z.infer<typeof schemaInscription>;

/**
 * La regle de la ville QUAND ELLE EST DEMANDEE, c'est-a-dire quand aucune session n'est retenue.
 *
 * ELLE SE REPREND DE `schemaDemande`, ELLE NE SE RECOPIE PAS : une seule contrainte pour les deux
 * formulaires, ce que `CONVENTIONS.md` § « Un seul schema de validation, partage » prescrit.
 */
export const schemaDeLaVille = schemaDemande.shape.ville;

/**
 * Le schema de la DEMANDE DE CONTACT, partage lui aussi par le navigateur et par la route.
 *
 * IL N'ETEND PAS `schemaDemande`, ET C'EST DELIBERE, alors que `schemaInscription` l'etend. Les deux
 * formulaires ne recueillent pas la meme chose : une question n'a ni ville, ni disponibilites, ni
 * consentement, et son telephone est FACULTATIF la ou celui d'une demande de place est obligatoire.
 * Etendre puis retirer quatre champs et en assouplir un cinquieme aurait laisse, dans le type comme
 * dans la validation, la trace d'un formulaire que cette page n'a pas.
 *
 * CE QU'IL PARTAGE VRAIMENT AVEC L'AUTRE : les codes d'erreur et le champ piege, qui sont les deux
 * choses que le composant et la route doivent lire de la meme facon.
 *
 * LE MESSAGE N'EST PAS BORNE EN LONGUEUR, comme la remarque du parcours : `contenu-site.md` v4
 * § Le formulaire de contact ne l'ecrit nulle part, `spec-technique.md` v6 § La route de contact non
 * plus, et la colonne `message` de la table est en `text`. Poser un maximum ici refuserait une
 * question sur une regle que personne n'a ecrite.
 *
 * `AC-CTC-002` LE MESURE PAR SON EFFET : il presente au schema ce que la route doit refuser, puis
 * poste la meme chose a la route, et exige le meme refus.
 */
export const schemaContact = z.object({
  prenomNom: z.string().trim().min(1, CODES.obligatoire),
  email: z.string().trim().min(1, CODES.obligatoire).email(CODES.email),
  telephone: z.string().trim().optional().default(''),
  message: z.string().trim().min(1, CODES.obligatoire),
  piege: z.string().max(0, CODES.piege).optional().default(''),
});

export type DemandeDeContact = z.infer<typeof schemaContact>;
